import { useQuery } from '@apollo/client/react';
import { Button, Center, Spinner, Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { GET_REACTIONS } from '#graphql/queries/reactions';
import { LoginPage } from '#pages/login';
import { ReactionsProvider } from '#providers/reactions-provider';
import { useUser } from '#providers/user-provider';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Loads the reaction catalog (GraphQL is auth-guarded) before rendering the app shell.
 */
function AuthenticatedReactionsGate({ children }: { children: ReactNode }) {
  const reactionsQ = useQuery(GET_REACTIONS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const reactions = reactionsQ.data?.reactions;
  const ready = reactions != null && Array.isArray(reactions);
  const hasError = reactionsQ.error != null;

  if (ready) {
    return <ReactionsProvider value={reactions}>{children}</ReactionsProvider>;
  }

  if (!hasError && reactionsQ.loading) {
    return (
      <Center minH="var(--app-viewport-height)">
        <Spinner size="xl" />
      </Center>
    );
  }

  const message =
    reactionsQ.error?.message ||
    'Something went wrong while loading reactions.';

  return (
    <Center minH="var(--app-viewport-height)">
      <VStack gap={4} maxW="md" px={4} textAlign="center">
        <Text fontWeight="semibold">Could not load the app</Text>
        <Text color="fg.muted" fontSize="sm">
          {message}
        </Text>
        <Button onClick={() => void reactionsQ.refetch()}>Try again</Button>
      </VStack>
    </Center>
  );
}

/**
 * Renders children only when the user is authenticated (valid JWT cookie).
 * Shows login page when not authenticated, and a loading state while checking.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <Center minH="var(--app-viewport-height)">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <AuthenticatedReactionsGate>{children}</AuthenticatedReactionsGate>;
}
